import { getTheme } from 'utils'
import { googlePayMerchantProfiles } from '../config'

// 本地开发也走 TEST：localhost 不是 Google 后台登记的域名，用 PRODUCTION 拉不起面板，
// 而且会用真卡生成令牌，和 SIM/UAT 后端对不上
const TEST_HOSTNAMES = [
  'sim-checkout.test2pay.com',
  'uat-checkout.test2pay.com',
  'localhost',
  '127.0.0.1',
]

const getEnvironment = () =>
  TEST_HOSTNAMES.includes(window.location.hostname) ? 'TEST' : 'PRODUCTION'

/**
 * Returns true when the page is running inside an Android WebView.
 * Google Pay's JS SDK blocks payment sheet rendering in this context (OR_BIBED_15).
 * Detection heuristic: the `wv` flag is injected by Android WebView into the UA string,
 * and the Version token (used by the system WebView) is absent in standalone Chrome.
 */
const isAndroidWebView = () => {
  const ua = navigator.userAgent || ''
  return /Android/.test(ua) && (/; wv\)/.test(ua) || /wv/.test(ua))
}

/**
 * Escapes the WebView by navigating to the current URL via an Android Intent,
 * which causes the OS to open it in Chrome Custom Tabs — a real browser context
 * that Google Pay fully supports.
 */
const openInChrome = () => {
  const { protocol, host, pathname, search, hash } = window.location
  const scheme = protocol.replace(':', '')
  const intentUrl =
    `intent://${host}${pathname}${search}${hash}` +
    `#Intent;scheme=${scheme};package=com.android.chrome;` +
    `S.browser_fallback_url=${encodeURIComponent(window.location.href)};end`
  window.location.href = intentUrl
}

/**
 * 后端 payMethodExtension.decryptType 用这个值表示令牌由我方自行解密，
 * 和 ApplePay / SamsungPay 送给后端的取值保持一致。
 */
export const MERCHANT_DECRYPT = 'MERCHANT_DECRYPT'

/**
 * 后端配置缺失这一类错误：拉不起面板是我方配置问题，不是 Google 或用户的问题。
 * 带上 googlePayConfig 标记，调用方据此决定提示"换一种支付方式"还是"再试一次"。
 */
const configError = (message) => {
  const error = new Error(message)
  error.googlePayConfig = true
  return error
}

/**
 * Google 返回的、重试也不会好的 statusCode：
 * - DEVELOPER_ERROR：请求参数不对（gateway 未登记、卡组织名不合法、域名未批准 …）
 * - MERCHANT_ACCOUNT_ERROR：Google Pay & Wallet Console 档案本身有问题（未批准、未登记当前域名）
 * 其余（BUYER_ACCOUNT_ERROR、INTERNAL_ERROR、弹窗被拦截的字符串错误）再试一次可能就好。
 */
const PERMANENT_GOOGLE_ERRORS = ['DEVELOPER_ERROR', 'MERCHANT_ACCOUNT_ERROR']

/**
 * 是否属于配置类错误（我方抛出的缺字段错误，或 Google 的永久性错误）。
 * 这类错误重试也不会好，提示用户换支付方式；其它提示再试一次。
 */
export const isGooglePayConfigError = (error) =>
  !!error && (PERMANENT_GOOGLE_ERRORS.includes(error.statusCode) || error.googlePayConfig === true)

/**
 * Google Pay 认识的卡组织（allowedCardNetworks 的合法取值）。后端的 allowCardBrands 是给整个卡
 * 支付方式用的，可能带 Jaywan / UnionPay 这类 Google 不收的品牌，交给 Google 会整单 DEVELOPER_ERROR。
 */
export const GOOGLE_PAY_CARD_NETWORKS = ['AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'MIR', 'VISA']

/**
 * Google Pay & Wallet Console 档案，统一维护在 src/config.js：
 * - PSP 线（我方自行解密）用 Botim Money 档案，正常由后端 googleMerchantId / googleMerchantName 下发；
 * - 老 PayBy 档案只给原有的网关解密链路继续用。
 */
export const BOTIM_MONEY_MERCHANT_INFO = googlePayMerchantProfiles.botimMoney
export const PAYBY_MERCHANT_INFO = googlePayMerchantProfiles.payBy

/**
 * 按后端下发的 decryptType 组装 tokenizationSpecification。两种模式都是 PAYMENT_GATEWAY：
 *
 * - MERCHANT_DECRYPT（Google 认定我们是 PSP，不能用商户 DIRECT 模式），后端下发：
 *     merchantId        -> gateway（Google 登记给我方的 PSP gatewayId，2026-10-07 起为 botimmoney）
 *     gatewayMerchantId -> gatewayMerchantId（本单商户在我方的商户号，Google 不校验，
 *                          原样签进报文，由后端解密后比对）
 * - 其它取值：保持原有的网关解密链路，gatewayName -> gateway，merchantId -> gatewayMerchantId。
 *
 * 自行解密时两个字段缺一个都直接抛错，不兜底、不回退到网关解密 —— 令牌会加密给错的网关
 * 或商户号对不上，后端只能拒绝；静默降级只会把配置问题藏进"支付失败"里。
 *
 * @param {object|null} [payMethodExtension] payMethod.payMethodExtension（null / undefined 按空对象处理）
 */
export const buildTokenizationSpecification = (payMethodExtension) => {
  // 后端可能下发 null，默认参数只对 undefined 生效，这里统一兜成空对象
  const extension = payMethodExtension || {}
  if (extension.decryptType === MERCHANT_DECRYPT) {
    if (!extension.merchantId) {
      throw configError(
        'Google Pay merchant decrypt is enabled but payMethodExtension.merchantId (PSP gateway id) is missing'
      )
    }
    if (!extension.gatewayMerchantId) {
      throw configError(
        'Google Pay merchant decrypt is enabled but payMethodExtension.gatewayMerchantId is missing'
      )
    }
    return {
      type: 'PAYMENT_GATEWAY',
      parameters: {
        gateway: extension.merchantId,
        gatewayMerchantId: extension.gatewayMerchantId,
      },
    }
  }
  return {
    type: 'PAYMENT_GATEWAY',
    parameters: {
      gateway: extension.gatewayName,
      gatewayMerchantId: extension.merchantId,
    },
  }
}

/**
 * 后端 allowCardBrands 是逗号分隔的卡组织（"VISA,MASTERCARD,JAYWAN"）。只保留 Google 认识的
 * 品牌；一个都不剩（缺失、空串、全是 Google 不收的）才抛配置错误 —— 空字符串或未知品牌交给
 * Google 会直接 DEVELOPER_ERROR，错误却看起来像 Google 的问题。
 *
 * 被丢掉的品牌通过 console.warn 留痕（如 JAYWAN），否则"某张卡在 Google Pay 里选不到"
 * 排查时看不出是这里过滤掉的。
 *
 * @param {object|null} [payMethodExtension] payMethod.payMethodExtension（null / undefined 按空对象处理）
 * @returns {string[]}
 */
export const buildAllowedCardNetworks = (payMethodExtension) => {
  const extension = payMethodExtension || {}
  const brands = `${extension.allowCardBrands || ''}`
    .split(',')
    .map((brand) => brand.trim().toUpperCase())
    .filter(Boolean)
  const networks = brands.filter((brand) => GOOGLE_PAY_CARD_NETWORKS.includes(brand))
  const dropped = brands.filter((brand) => !GOOGLE_PAY_CARD_NETWORKS.includes(brand))
  if (dropped.length) {
    console.warn(`Google Pay: card brands not supported by Google Pay were dropped: ${dropped.join(',')}`)
  }
  if (!networks.length) {
    throw configError(
      `Google Pay is enabled but payMethodExtension.allowCardBrands has no Google Pay card network (got "${extension.allowCardBrands || ''}")`
    )
  }
  return networks
}

/**
 * 按 decryptType 选 Google Console 档案：
 * 自行解密取后端下发的 googleMerchantId / googleMerchantName（缺省兜底 Botim Money 档案），
 * 其余沿用老 PayBy 档案。
 *
 * @param {object|null} [payMethodExtension] payMethod.payMethodExtension（null / undefined 按空对象处理）
 */
export const buildMerchantInfo = (payMethodExtension) => {
  const extension = payMethodExtension || {}
  if (extension.decryptType !== MERCHANT_DECRYPT) return { ...PAYBY_MERCHANT_INFO }
  return {
    merchantId: extension.googleMerchantId || BOTIM_MONEY_MERCHANT_INFO.merchantId,
    merchantName: extension.googleMerchantName || BOTIM_MONEY_MERCHANT_INFO.merchantName,
  }
}

/**
 * 取 Google Pay 报错的可读描述，用于埋点上报。
 * Google 的错误通常是 { statusCode, statusMessage }，但也会直接抛字符串
 * （如弹窗被浏览器拦截时的 "failed to open window"），两种都要能取到内容。
 *
 * @param {*} error loadPaymentData 抛出的错误
 * @returns {string|undefined}
 */
export const describeGooglePayError = (error) => {
  if (typeof error === 'string') return error
  if (!error || typeof error !== 'object') return undefined
  return error.statusMessage || error.message || undefined
}

const googlePayRequest = {
  baseRequest: {
    apiVersion: 2,
    apiVersionMinor: 0,
  },
  allowedCardAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
  // tokenizationSpecification: {
  //   type: 'PAYMENT_GATEWAY',
  //   parameters: {
  //     gateway: 'mpgs',
  //     gatewayMerchantId: '120810000105',
  //   },
  // },
  merchantInfo: PAYBY_MERCHANT_INFO,
  allowedCardNetworks: GOOGLE_PAY_CARD_NETWORKS,
  init: function() {
    if (!this.paymentsClient) {
      this.paymentsClient = new window.google.payments.api.PaymentsClient({
        environment: getEnvironment(),
      })
    }
  },
  createButton: function(containerId, onClick) {
    this.init()
    const container = document.getElementById(containerId)

    const safeClick = isAndroidWebView()
      ? (e) => { e.preventDefault(); openInChrome() }
      : onClick

    // Brand guidelines: dark (black) on light backgrounds; light (white) on dark.
    // Do not restyle .gpay-button via CSS — use createButton options only.
    if (container) {
      container.innerHTML = ''
    }
    const button = this.paymentsClient.createButton({
      buttonColor: getTheme() === 'dark' ? 'white' : 'black',
      buttonType: 'plain',
      buttonLocale: 'en',
      buttonSizeMode: 'fill',
      buttonRadius: 8,
      onClick: safeClick,
    })
    container.appendChild(button)
  },
  /**
   * 检查 Google Pay 是否可用（设备层面；不校验用户钱包里是否已有匹配的卡）。
   * 传入商户允许的卡组织（和拉起面板时一致）限制卡组织范围；不传则按 Google 全量卡组织检查。
   * 若需在钱包无匹配卡时也隐藏按钮，需在 IsReadyToPayRequest 中加 existingPaymentMethodRequired: true。
   */
  isReadyToPay: async function(allowedCardNetworks) {
    this.init()

    const getGoogleIsReadyToPayRequest = {
      ...this.baseRequest,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: this.allowedCardAuthMethods,
            allowedCardNetworks: allowedCardNetworks || this.allowedCardNetworks,
          },
        },
      ],
    }
    const res = await this.paymentsClient.isReadyToPay(
      getGoogleIsReadyToPayRequest
    )
    return res && res.result
  },
  loadPaymentDate: async function(data) {
    this.init()
    // 拉起交易

    this.paymentDataRequest = {
      ...this.baseRequest,
      merchantInfo: data.merchantInfo || this.merchantInfo,
      allowedPaymentMethods: [
        {
          tokenizationSpecification: data.tokenizationSpecification,
          type: 'CARD',
          parameters: {
            allowedAuthMethods: this.allowedCardAuthMethods,
            allowedCardNetworks: data.allowedCardNetworks,
            allowCreditCards: data.debitCredit !== 'DC',
          },
        },
      ],
      transactionInfo: data.transactionInfo,
    }

    try {
      const res = await this.paymentsClient.loadPaymentData(
        this.paymentDataRequest
      )
      return {
        paymentToken: res.paymentMethodData.tokenizationData.token,
        cardNoSuffix: res.paymentMethodData.info.cardDetails,
      }
    } catch (error) {
      // 用户主动关掉面板不算错误；其它（如档案或网关配置不对导致的 DEVELOPER_ERROR）交给调用方上报和提示
      if (error && error.statusCode === 'CANCELED') return undefined
      throw error
    }
  },
}

export default googlePayRequest
